import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, trackPageView } from '@/api/flywheel';
import { useAuthStore } from '@/store/auth';

/**
 * Fire a `page_view` analytics event on every route change, and a
 * matching `page_dwell` event (with duration_ms) when the user leaves
 * that page. Mount once at the top of the router tree.
 *
 * The "product" is inferred from the first path segment when it matches
 * a known ARGOS product; otherwise it's recorded as "platform".
 */

const PRODUCT_SLUGS = new Set([
  'polygon',
  'phreatic',
  'pathfinder',
  'permeate',
  'perpetual',
  'protocol',
  'precept',
  'precipitate',
  'pipeline',
  'plunge',
  'pluvius',
  'pond',
  'pounce',
  'proton',
  'proxy',
  'purge',
  'pyritic',
  'data-vault',
  'terra',
]);

function inferProduct(pathname: string): string {
  const segment = pathname.replace(/^\//, '').split('/')[0] ?? '';
  return PRODUCT_SLUGS.has(segment) ? segment : 'platform';
}

export function usePageTracking(): void {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const enteredAtRef = useRef<number>(Date.now());
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Telemetry events are tenant-scoped (client_id). The backend
    // /flywheel/events endpoint correctly rejects anonymous calls with 401.
    // Firing them from public auth pages would create a 401 → refresh →
    // redirect-to-/login loop in the apiClient interceptor.
    if (!isAuthenticated) {
      prevPathRef.current = location.pathname;
      enteredAtRef.current = Date.now();
      return;
    }

    const now = Date.now();

    if (prevPathRef.current && prevPathRef.current !== location.pathname) {
      const duration = now - enteredAtRef.current;
      trackEvent({
        event_type: 'page_dwell',
        product: inferProduct(prevPathRef.current),
        entity_type: 'page',
        entity_id: prevPathRef.current,
        duration_ms: duration,
      });
    }

    trackPageView(inferProduct(location.pathname), location.pathname, {
      search: location.search || undefined,
    });

    prevPathRef.current = location.pathname;
    enteredAtRef.current = now;
  }, [location.pathname, location.search, isAuthenticated]);
}
